import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import type { FormDefinition, FormResponsePlaintext } from '../types/forms';
import type { BusinessListing } from '../types';
import { extractSubdomain, getBusinessListing } from '../utils/firebase';
import { getFormByCode, getFormEncryptionKey, submitFormResponse } from '../utils/formApi';
import { encryptFormResponse } from '../utils/formEncryption';
import { executeRecaptchaV3 } from '../utils/recaptcha';
import { FormRenderer } from '../components/forms/FormRenderer';
import { FormBusinessHeader } from '../components/forms/FormBusinessHeader';
import { useBusinessPageTitle } from '../utils/pageTitle';
import { siteColorsFor, useSiteColorVariables } from '../hooks/useSiteColors';

type PageState = 'loading' | 'ready' | 'form_not_found' | 'key_error' | 'error';

export function FormPage() {
  const { formCode } = useParams<{ formCode: string }>();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('t') ?? undefined;

  const [pageState, setPageState] = useState<PageState>('loading');
  const [listing, setListing] = useState<BusinessListing | null>(null);
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [publicKeyB64, setPublicKeyB64] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useBusinessPageTitle(listing);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const subdomain = extractSubdomain(window.location.hostname);
        if (!subdomain) {
          setPageState('error');
          return;
        }

        const listingData = await getBusinessListing(subdomain);
        if (!listingData) {
          setPageState('error');
          return;
        }

        const [formData, keyData] = await Promise.all([
          getFormByCode(listingData.id, formCode!),
          getFormEncryptionKey(listingData.id),
        ]);

        if (cancelled) return;

        setListing(listingData);

        if (!formData) {
          setPageState('form_not_found');
          return;
        }

        if (!keyData) {
          setForm(formData);
          setPageState('key_error');
          return;
        }

        setForm(formData);
        setPublicKeyB64(keyData.publicKeyB64);
        setPageState('ready');
      } catch (err) {
        if (!cancelled) {
          console.error('FormPage load error:', err);
          setPageState('error');
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [formCode]);

  const colors = useMemo(() => siteColorsFor(listing), [listing]);
  useSiteColorVariables(colors);

  const handleSubmit = async (data: FormResponsePlaintext) => {
    if (!form || !publicKeyB64 || !listing) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      const encrypted = encryptFormResponse(data, publicKeyB64);

      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await executeRecaptchaV3('form_submit');
      } catch {
        setSubmitError("We couldn't verify your request. Please reload and try again.");
        setSubmitting(false);
        return;
      }

      const result = await submitFormResponse({
        practitionerId: listing.id,
        token: urlToken,
        recaptchaToken,
        responses: [{
          formId: form.id,
          formType: form.type,
          ciphertext: encrypted.ciphertextB64,
          nonce: encrypted.nonceB64,
          ephemeralPublicKey: encrypted.ephemeralPublicKeyB64,
        }],
      });

      if (result.success) {
        setSubmitted(true);
      } else {
        setSubmitError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-site-bg text-site-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-site-muted mx-auto mb-4" />
          <p className="text-site-muted">Loading form…</p>
        </div>
      </div>
    );
  }

  if (pageState === 'form_not_found') {
    return (
      <div className="min-h-screen bg-site-bg text-site-text flex items-center justify-center p-4">
        {listing && <FormBusinessHeader listing={listing} />}
        <div className="max-w-md w-full bg-site-bg rounded-2xl border border-site-border p-8 text-center">
          <div className="w-16 h-16 bg-site-text/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📋</span>
          </div>
          <h1 className="text-2xl font-bold text-site-text mb-2">Form Not Available</h1>
          <p className="text-site-muted mb-6">This form is no longer available.</p>
          <a href="/" className="text-site-text hover:text-site-text font-medium underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  if (pageState === 'key_error') {
    return (
      <div className="min-h-screen bg-site-bg text-site-text">
        {listing && <FormBusinessHeader listing={listing} />}
        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center">
            <div className="w-16 h-16 bg-site-warning-soft rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="text-xl font-bold text-site-text mb-2">Form Unavailable</h1>
            <p className="text-site-muted">
              This form can't accept responses right now. Please contact the business directly.
            </p>
            {listing?.public_phone && (
              <p className="mt-4 text-sm text-site-muted">
                Phone: <a href={`tel:${listing.public_phone}`} className="font-medium text-site-text">{listing.public_phone}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-site-bg text-site-text flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-site-bg rounded-2xl border border-site-border p-8 text-center">
          <div className="w-16 h-16 bg-site-danger-soft rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-bold text-site-text mb-2">Something went wrong</h1>
          <p className="text-site-muted mb-6">We couldn't load this page. Please try again later.</p>
          <a href="/" className="text-site-text hover:text-site-text font-medium underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-site-bg text-site-text">
        {listing && <FormBusinessHeader listing={listing} />}
        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-site-primary/10">
              <CheckCircle className="w-8 h-8 text-site-link" />
            </div>
            <h1 className="text-2xl font-bold text-site-text mb-2">Thank you!</h1>
            <p className="text-site-muted">Your response has been submitted.</p>
            {listing?.public_phone && (
              <p className="mt-6 text-sm text-site-muted">
                Questions? Contact us at{' '}
                <a href={`tel:${listing.public_phone}`} className="font-medium text-site-text">
                  {listing.public_phone}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-site-bg text-site-text">
      {listing && <FormBusinessHeader listing={listing} />}

      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-site-bg rounded-2xl border border-site-border p-6">
          {submitError && (
            <div className="mb-6 p-4 bg-site-danger-soft border border-site-danger/40 rounded-xl">
              <p className="text-sm text-site-danger">{submitError}</p>
            </div>
          )}

          {form && (
            <FormRenderer
              form={form}
              hideContactFields={!!urlToken}
              submitLabel="Submit"
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
}
